import { ANKOTISH_PAGE_IDS, SADHANA_PAGE_IDS } from '../seo/pageRoutes';

export type SearchSectionId = 'jyotisha' | 'numerology' | 'sadhana';

export type SearchSectionConfig = {
  id: SearchSectionId;
  labelEn: string;
  labelHi: string;
  searchLabelEn: string;
  searchLabelHi: string;
  placeholderEn: string;
  placeholderHi: string;
  pageIds: Set<string>;
  defaultPageId: string;
  hasSearchIndex: boolean;
  comingSoon?: boolean;
};

const JYOTISHA_PAGE_IDS = new Set(['jyotisha', 'jyotish-marga', 'phaladeepika']);

export const SEARCH_SECTIONS: SearchSectionConfig[] = [
  {
    id: 'jyotisha',
    labelEn: 'Jyotisha',
    labelHi: 'ज्योतिष',
    searchLabelEn: 'Search Jyotisha topics',
    searchLabelHi: 'ज्योतिष विषय खोजें',
    placeholderEn: 'Search Jyotisha topics…',
    placeholderHi: 'ज्योतिष विषय खोजें…',
    pageIds: JYOTISHA_PAGE_IDS,
    defaultPageId: 'jyotisha',
    hasSearchIndex: true,
  },
  {
    id: 'numerology',
    labelEn: 'Ankotish',
    labelHi: 'अंकोतिष',
    searchLabelEn: 'Search Ankotish topics',
    searchLabelHi: 'अंकोतिष विषय खोजें',
    placeholderEn: 'Search Ankotish topics…',
    placeholderHi: 'अंकोतिष विषय खोजें…',
    pageIds: ANKOTISH_PAGE_IDS,
    defaultPageId: 'bhagyank',
    hasSearchIndex: true,
  },
  {
    id: 'sadhana',
    labelEn: 'Sadhana',
    labelHi: 'साधना',
    searchLabelEn: 'Search Sadhana mantras',
    searchLabelHi: 'साधना मंत्र खोजें',
    placeholderEn: 'Search mantras…',
    placeholderHi: 'मंत्र खोजें…',
    pageIds: SADHANA_PAGE_IDS,
    defaultPageId: 'sadhana',
    hasSearchIndex: false,
    comingSoon: true,
  },
];

export const SEARCH_SECTION_BY_ID = Object.fromEntries(
  SEARCH_SECTIONS.map((section) => [section.id, section]),
) as Record<SearchSectionId, SearchSectionConfig>;

export const TOPIC_SEARCH_OPEN_EVENT = 'open-topic-search';

export type TopicSearchOpenDetail = {
  pageId?: string;
  sectionId?: SearchSectionId;
  initialQuery?: string;
  /** When set (e.g. nav tour), narrow results to this topic id if it appears in matches. */
  tourSelectTopicId?: string;
};

/**
 * Resolve locked search section for a page, or null when section picker should show.
 */
export function getSearchSectionForPage(pageId: string): SearchSectionId | null {
  for (const section of SEARCH_SECTIONS) {
    if (section.pageIds.has(pageId)) return section.id;
  }
  return null;
}

/**
 * Whether the modal should show the section picker (neutral pages).
 */
export function resolveSearchSection(pageId: string): SearchSectionId | 'picker' {
  const locked = getSearchSectionForPage(pageId);
  if (locked) {
    const section = SEARCH_SECTION_BY_ID[locked];
    if (section.hasSearchIndex) return locked;
    return 'picker';
  }
  return 'picker';
}

export function getSearchSectionConfig(sectionId: SearchSectionId): SearchSectionConfig {
  return SEARCH_SECTION_BY_ID[sectionId];
}

export function getSearchableSections(): SearchSectionConfig[] {
  return SEARCH_SECTIONS.filter((section) => section.hasSearchIndex);
}

export function getPickerSections(): SearchSectionConfig[] {
  return SEARCH_SECTIONS.filter((section) => section.hasSearchIndex);
}

export function getSearchPlaceholder(pageId: string, showEnglish: boolean): string {
  const mode = resolveSearchSection(pageId);
  if (mode === 'picker') {
    return showEnglish ? 'Search topics…' : 'विषय खोजें…';
  }
  const section = SEARCH_SECTION_BY_ID[mode];
  return showEnglish ? section.placeholderEn : section.placeholderHi;
}

export function openTopicSearch(detail: TopicSearchOpenDetail = {}) {
  window.dispatchEvent(new CustomEvent(TOPIC_SEARCH_OPEN_EVENT, { detail }));
}

/** Legacy event aliases for gradual migration. */
export function installLegacySearchEventAliases() {
  const onJyotisha = () => openTopicSearch({ sectionId: 'jyotisha' });
  const onNumerology = () => openTopicSearch({ sectionId: 'numerology' });
  window.addEventListener('open-jyotisha-search-modal', onJyotisha);
  window.addEventListener('open-numerology-search-modal', onNumerology);
  return () => {
    window.removeEventListener('open-jyotisha-search-modal', onJyotisha);
    window.removeEventListener('open-numerology-search-modal', onNumerology);
  };
}
